import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { Feature } from 'src/replicant/analytics';
import Scroll from 'src/game/components/shared/Scroll';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  GachaProbabilityItem,
  GachaProbabilityItemData,
} from './GachaProbabilityItem';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import {
  GachaTag,
  gachaEventSchedule,
  gachasById,
} from 'src/replicant/gacha/gacha.ruleset';
import { now } from 'src/StateObserver';

import { AssetGroup } from '@play-co/timestep-core';
export default class PopupGachaInfo extends PopupBasic {
  private buttonOkay: ButtonScaleViewWithText;
  private scroll: Scroll<GachaProbabilityItemData, GachaProbabilityItem>;
  private tier: LangBitmapFontTextView;
  private probability: LangBitmapFontTextView;
  private note: LangBitmapFontTextView;
  private probabilityTip: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 11000,
      width: 558,
      height: 890,
      darkerBg: false,
      tracking: { feature: Feature.GACHA._, $subFeature: Feature.GACHA.INFO },
    });

    this.buttonClose.removeFromSuperview();

    this.probabilityTip = new LangBitmapFontTextView({
      superview: this.box,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      color: '#BF8147',
      width: this.box.style.width - 50,
      height: 80,
      x: (this.box.style.width - (this.box.style.width - 50)) / 2,
      y: 76,
    });

    const scrollBox = new ImageScaleView({
      ...uiConfig.popups.scrollBox,
      superview: this.box,
      x: 30,
      y: 180,
      width: this.box.style.width - 60,
      height: this.box.style.height - 360,
    });

    const header = new FlexBoxView({
      superview: scrollBox,
      width: scrollBox.style.width,
      height: 50,
      y: -40,
      flex: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingLeft: 16,
        paddingRight: 16,
      },
    });

    this.tier = new LangBitmapFontTextView({
      superview: header,
      align: 'left',
      verticalAlign: 'center',
      size: 22,
      font: 'Body',
      color: '#9A7171',
      width: header.style.width * 0.5,
      height: header.style.height,
    });

    this.probability = new LangBitmapFontTextView({
      superview: header,
      align: 'right',
      verticalAlign: 'center',
      size: 22,
      font: 'Body',
      color: '#9A7171',
      width: header.style.width * 0.5,
      height: header.style.height,
    });

    this.message.updateOpts({
      width: this.box.style.width - 80,
      height: 315,
      x: 40,
      y: 55,
      zIndex: 1,
      color: '#9C490D',
      wordWrap: true,
    });

    this.scroll = new Scroll<GachaProbabilityItemData, GachaProbabilityItem>({
      superview: scrollBox,
      recycle: true,
      rect: {
        x: 0,
        y: 5,
        width: scrollBox.style.width,
        height: scrollBox.style.height - 7,
      },
      createItem: (superview) => {
        return new GachaProbabilityItem({
          superview,
          x: 10,
          width: scrollBox.style.width - 20,
          height: 50,
        });
      },
      setData: (_, data, item) => {
        item.setData(data);
      },
    });

    this.note = new LangBitmapFontTextView({
      superview: scrollBox,
      align: 'center',
      verticalAlign: 'top',
      size: 18,
      font: 'Body',
      color: '#BF8147',
      width: scrollBox.style.width,
      height: 50,
      y: scrollBox.style.height + 10,
    });

    this.buttonOkay = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: -1,
      fontSize: 31,
      font: 'Body',
      x: this.box.style.width / 2,
      y: this.box.style.height - 70,
      width: 200,
      height: 80,
      centerOnOrigin: true,
      onClick: async () => opts.close(),
      tracking: {
        feature: Feature.GACHA._,
        $subFeature: Feature.GACHA.INFO_OK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup(['assets/ui/popups/scroller_panel.png']);
  }

  public init() {
    super.init({ title: i18n('gacha.popupInfo.title') });
    this.tier.localeText = () => i18n('gacha.popupInfo.tier');
    this.probability.localeText = () => i18n('gacha.popupInfo.probability');
    this.note.localeText = () => i18n('gacha.popupInfo.note');
    this.buttonOkay.localeText = () => i18n('basic.okay');
    this.probabilityTip.localeText = () =>
      i18n('gacha.popupInfo.probabilityTip');
    this.scroll.setItems(this.getData());
  }

  private getData(): GachaProbabilityItemData[] {
    const currentEvent = gachaEventSchedule.getCurrentEvent(now());
    if (!currentEvent?.metadata) return [];

    // There are multiple gachas under the same tag, but they have the same values
    const gacha = getGachaByTag(currentEvent.metadata.tag);
    if (!gacha) return [];

    const total = Object.values(gacha.buckets).reduce((a, b) => a + b, 0);
    const toProbabilityItem = (id: string) => ({
      tier: Number(id.match(/tier(\d+)/)?.[1]),
      probability: 100 * (gacha.buckets[id] / total),
    });

    // Find out the probabilities for each type and tier
    const eventDecos: GachaProbabilityItemData[] = Object.keys(gacha.buckets)
      .filter((x) => !x.includes('regular'))
      .map(toProbabilityItem);
    const regularDecos: GachaProbabilityItemData[] = Object.keys(gacha.buckets)
      .filter((x) => x.includes('regular'))
      .map(toProbabilityItem);

    // List all items
    const items: GachaProbabilityItemData[] = [
      {
        title: i18n('gacha.popupInfo.eventDecoration'),
      },
      ...eventDecos,
      {
        title: i18n('gacha.popupInfo.regularDecoration'),
      },
      ...regularDecos,
    ];

    return items;
  }
}

function getGachaByTag(tag: GachaTag) {
  for (const gacha of Object.values(gachasById)) {
    if (gacha.tags?.includes(tag)) {
      return gacha;
    }
  }
  return null;
}
